import { api } from "@/lib/api";

// Shared Web Push client used by NotificationBell and SubscribePrompt.
// One code path → identical behaviour on every device.

export const SITE_URL = "https://alisalemps.com/";

export function urlB64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

function u8Equal(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

// Environment classification — capability checks first, UA hints second.
//  kind: "ok" | "insecure" | "unsupported" | "in-app" | "ios-browser"
export function detectPushEnv() {
    if (typeof window === "undefined") return { kind: "unsupported", reason: "ssr" };
    const ua = navigator.userAgent || "";
    const isIOS = /iP(hone|ad|od)/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
    const isAndroid = /Android/i.test(ua);
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
    const inApp = /FBAN|FBAV|FB_IAB|Instagram|TikTok|musical_ly|BytedanceWebview|Snapchat|Twitter|; wv\)|Line\/|MicroMessenger|Pinterest/i.test(ua);
    const hasSW = "serviceWorker" in navigator;
    const hasPush = "PushManager" in window;
    const hasNotif = "Notification" in window;
    if (!window.isSecureContext) return { kind: "insecure", isIOS, isAndroid, standalone };
    if (isIOS && !standalone) return { kind: "ios-browser", isIOS, isAndroid, standalone };
    if (inApp && (!hasSW || !hasPush || !hasNotif || isAndroid)) return { kind: "in-app", isIOS, isAndroid, standalone, inApp: true };
    if (!hasSW || !hasPush || !hasNotif) return { kind: "unsupported", isIOS, isAndroid, standalone, inApp };
    return { kind: "ok", isIOS, isAndroid, standalone, inApp: false };
}

export const IN_APP_MESSAGE = "لتفعيل الإشعارات، افتح الموقع مباشرةً في تطبيق Google Chrome.";

export async function copySiteLink() {
    try { await navigator.clipboard.writeText(SITE_URL); return true; } catch { return false; }
}

export class PushError extends Error {
    constructor(stage, name, message, hint) {
        super(message);
        this.stage = stage; this.errName = name; this.hint = hint;
    }
}

export const ERROR_TEXT = {
    NotAllowedError: "تم رفض إذن الإشعارات من المتصفح — اسمح بالإشعارات لهذا الموقع من إعدادات المتصفح ثم أعد المحاولة.",
    AbortError: "تعذّر اتصال المتصفح بخدمة الإشعارات الفورية أو لم تكتمل عملية الاشتراك. حدّث Google Chrome وخدمات Google Play ثم جرّب من شبكة أخرى.",
    InvalidStateError: "حالة خدمة الإشعارات في المتصفح غير مستقرة — أُعيد تجهيزها، اضغط الجرس مرة أخرى.",
    NotSupportedError: "هذا المتصفح لا يدعم الإشعارات الفورية — استخدم Google Chrome.",
    default: "تعذّر تفعيل الإشعارات الفورية — أعد المحاولة بعد لحظات.",
};

export function errorText(e) {
    return ERROR_TEXT[e?.errName || e?.name] || ERROR_TEXT.default;
}

async function fetchServerKey() {
    const r = await api.get("/public/push/vapid-public-key");
    const pub = r?.data?.public_key;
    if (r?.data?.disabled || !pub) throw new PushError("vapid", "ServerDisabled", "خدمة الإشعارات غير مفعّلة على الخادم حالياً.");
    const arr = urlB64ToUint8Array(pub);
    if (arr.length !== 65 || arr[0] !== 4) throw new PushError("vapid", "BadVapidKey", "مفتاح الإشعارات على الخادم غير صالح.");
    return arr;
}

export async function ensureRegistration() {
    let reg = await navigator.serviceWorker.getRegistration("/");
    if (!reg) reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    if (!reg.active) {
        // Wait (bounded) for the worker to reach "activated".
        await new Promise((resolve) => {
            const sw = reg.installing || reg.waiting;
            if (!sw) return resolve();
            const t = setTimeout(resolve, 8000);
            sw.addEventListener("statechange", () => { if (sw.state === "activated") { clearTimeout(t); resolve(); } });
        });
    }
    return reg;
}

export async function getSwVersion(reg) {
    try {
        const url = reg?.active?.scriptURL || "";
        const r = await fetch(url || "/sw.js", { cache: "no-store" });
        const m = (await r.text()).match(/SW_VERSION\s*=\s*"([^"]+)"/);
        return m ? m[1] : null;
    } catch { return null; }
}

export async function saveSubscription(sub) {
    const json = sub.toJSON();
    const lang = (typeof window !== "undefined" && /^\/(en|es|nl)(\/|$)/.exec(window.location.pathname)?.[1]) || "ar";
    const { getDeviceHints } = await import("@/lib/deviceHints");
    const hints = await getDeviceHints();
    return api.post("/public/push/subscribe", { endpoint: json.endpoint, keys: json.keys, lang, hints });
}

// Best-effort diagnostics (admin log only). Never sends endpoints, keys or IPs.
export async function reportDiag(payload) {
    try {
        const env = detectPushEnv();
        await api.post("/public/push/diag", {
            stage: payload.stage || "unknown",
            error_name: payload.errName || payload.name || "unknown",
            sw_version: payload.swVersion || null,
            env_kind: env.kind,
            platform: env.isIOS ? "ios" : env.isAndroid ? "android" : "desktop",
            standalone: !!env.standalone,
            permission: typeof Notification !== "undefined" ? Notification.permission : "n/a",
        });
    } catch { /* diagnostics must never break the flow */ }
}

/**
 * Full subscribe flow. Resolves with { sub, reused } ONLY after the browser
 * subscription exists AND the server accepted it. Throws PushError otherwise.
 * The permission prompt is triggered here — call only from a user gesture.
 */
export async function subscribeToPush() {
    const env = detectPushEnv();
    if (env.kind !== "ok") throw new PushError("env", env.kind === "in-app" ? "InAppBrowser" : "NotSupportedError", IN_APP_MESSAGE);

    if (Notification.permission === "denied") throw new PushError("permission", "NotAllowedError", ERROR_TEXT.NotAllowedError);
    if (Notification.permission === "default") {
        const perm = await Notification.requestPermission();
        if (perm === "denied") throw new PushError("permission", "NotAllowedError", ERROR_TEXT.NotAllowedError);
        if (perm !== "granted") throw new PushError("permission", "Dismissed", "");
    }

    const serverKey = await fetchServerKey();

    let reg;
    try { reg = await ensureRegistration(); }
    catch (e) { throw new PushError("sw", e?.name || "SWError", "تعذّر تحميل خدمة الإشعارات على المتصفح."); }

    // Reuse a valid existing subscription. Only replace it when we can READ
    // its key and it differs from ours — never drop a working subscription
    // just because the browser hides options.applicationServerKey (Android).
    let sub = await reg.pushManager.getSubscription();
    let reused = false;
    if (sub) {
        const k = sub.options?.applicationServerKey ? new Uint8Array(sub.options.applicationServerKey) : null;
        if (!k || u8Equal(k, serverKey)) reused = true;
        else {
            try { await sub.unsubscribe(); } catch {}
            await new Promise((r) => setTimeout(r, 400)); // let the push service settle
            sub = null;
        }
    }
    if (!sub) {
        try {
            sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: serverKey });
        } catch (e) {
            if (e?.name === "InvalidStateError") {
                // One controlled reset of THIS device's subscription only.
                try { const stale = await reg.pushManager.getSubscription(); if (stale) await stale.unsubscribe(); } catch {}
                throw new PushError("subscribe", "InvalidStateError", ERROR_TEXT.InvalidStateError);
            }
            throw new PushError("subscribe", e?.name || "SubscribeError", errorText(e));
        }
    }

    try { await saveSubscription(sub); }
    catch (e) {
        const status = e?.response?.status;
        throw new PushError("save", `SaveFailed${status ? `_${status}` : ""}`, `تعذّر حفظ الاشتراك على الخادم${status ? ` (${status})` : ""}.`);
    }
    return { sub, reused, reg };
}
